import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import HomeworkInfo from '@/components/organisms/Card/Homework/HomeworkInfo';
import HomeworkScore, { GradingResultSummary } from '@/components/organisms/Card/Homework/HomeworkScore';
import { adminLearningScheduleApi, academyLearningScheduleApi } from 'services/api';

const AdminLearningGradingDetail = () => {
  const navigate = useNavigate();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [gradingResult, setGradingResult] = useState<GradingResultSummary | null>(null);
  const [bulkAiLoading, setBulkAiLoading] = useState(false);
  const bulkAiRef = useRef<{ runBulkAiGrading?: () => Promise<void> } | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [scheduleId]);

  useEffect(() => {
    const originalApis = {
      gradingDetail: academyLearningScheduleApi.gradingDetail,
      completeGrading: academyLearningScheduleApi.completeGrading,
      resendReport: academyLearningScheduleApi.resendReport,
      aiGradeDictation: academyLearningScheduleApi.aiGradeDictation,
      aiGradeFreeWriting: academyLearningScheduleApi.aiGradeFreeWriting,
    };

    academyLearningScheduleApi.gradingDetail = adminLearningScheduleApi.gradingDetail;
    academyLearningScheduleApi.completeGrading = adminLearningScheduleApi.completeGrading;
    academyLearningScheduleApi.resendReport = adminLearningScheduleApi.resendReport;
    academyLearningScheduleApi.aiGradeDictation = adminLearningScheduleApi.aiGradeDictation;
    academyLearningScheduleApi.aiGradeFreeWriting = adminLearningScheduleApi.aiGradeFreeWriting;

    return () => {
      academyLearningScheduleApi.gradingDetail = originalApis.gradingDetail;
      academyLearningScheduleApi.completeGrading = originalApis.completeGrading;
      academyLearningScheduleApi.resendReport = originalApis.resendReport;
      academyLearningScheduleApi.aiGradeDictation = originalApis.aiGradeDictation;
      academyLearningScheduleApi.aiGradeFreeWriting = originalApis.aiGradeFreeWriting;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!scheduleId) {
        setLoading(false);
        return;
      }

      try {
        const response = await adminLearningScheduleApi.gradingDetail(scheduleId);
        if (response.success) {
          setData(response.data);
        }
      } catch (error) {
        console.error('관리자 채점 상세 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scheduleId]);

  if (loading) {
    return (
      <AdminLayout>
        <main className='py-20 flex items-center justify-center'>
          <p>로딩 중...</p>
        </main>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <main className='py-20 flex items-center justify-center'>
          <p>데이터를 불러올 수 없습니다.</p>
        </main>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <main className='p-6'>
        <div className='flex justify-between items-center pb-5'>
          <div>
            <div className='flex items-center gap-4'>
              <button
                className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-200 px-4 py-2 shadow-sm mb-4'
                onClick={() => navigate(-1)}
              >
                <span className='text-lg leading-none'>&larr;</span>
                <span className='font-medium'>뒤로가기</span>
              </button>
            </div>
            <h1 className='text-2xl font-bold'>숙제 채점 상세</h1>
            {data.academyName && (
              <p className='text-sm text-gray-500 mt-1'>학원명: {data.academyName}</p>
            )}
          </div>
        </div>
        <div className='flex flex-col lg:flex-row lg:items-start lg:gap-8'>
          <section className='order-2 lg:order-1 lg:flex-1 lg:min-w-[640px]'>
            <div className='w-full border border-gray-100 rounded-2xl bg-white'>
              <HomeworkScore
                ref={bulkAiRef as any}
                type='grading'
                platform='admin'
                data={data}
                onStatusChange={(status) => setData((prev: any) => prev ? { ...prev, status } : prev)}
                onGradingResultChange={setGradingResult}
              />
            </div>
          </section>
          <section className='order-1 lg:order-2 lg:sticky lg:top-5 lg:max-w-[480px] card p-5 bg-white'>
            <HomeworkInfo
              type='grading'
              platform='admin'
              data={data}
              gradingResult={gradingResult}
              bulkAiLoading={bulkAiLoading}
              onBulkAiGrading={async () => {
                if (bulkAiLoading) return;
                setBulkAiLoading(true);
                try {
                  await bulkAiRef.current?.runBulkAiGrading?.();
                } catch (e) {
                  alert('AI 일괄 채점 중 오류가 발생했습니다.');
                } finally {
                  setBulkAiLoading(false);
                }
              }}
            />
          </section>
        </div>
      </main>
    </AdminLayout>
  );
};

export default AdminLearningGradingDetail;

